import { Button, Card, CardContent } from "@exhousing/design-system";
import { Repeat } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  useContractBilling,
  useStartBilling,
  useUpdateBilling,
} from "@/hooks/api/useContractBilling";
import { money, vatPct } from "@/lib/money";

/** Recurring-billing setup for a contract. Prefilled from the contract (convention),
 *  started by an explicit action, then visible and adjustable. */
export function BillingPanel({ contractId }: { contractId: string }) {
  const { data, isLoading } = useContractBilling(contractId);
  const start = useStartBilling(contractId);
  const update = useUpdateBilling(contractId);
  const [autoIssue, setAutoIssue] = useState(true);

  if (isLoading || !data) return null;

  const {
    schedule,
    suggested_lines: suggested,
    suggested_day_of_month: suggestedDay,
    suggested_start_date: suggestedStart,
  } = data;

  const onStart = () => {
    if (suggested.length === 0) {
      toast.error("Set rent/service costs on the contract first.");
      return;
    }
    start.mutate(
      {
        // Omit day_of_month — the server adopts the contract's payment_day convention.
        start_date: suggestedStart ?? new Date().toISOString().slice(0, 10),
        due_days: 14,
        auto_issue: autoIssue,
      },
      {
        onSuccess: () => toast.success("Billing started — invoices will generate monthly"),
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const toggleAutoIssue = () =>
    schedule &&
    update.mutate(
      { auto_issue: !schedule.auto_issue },
      { onError: (e) => toast.error(e.message) },
    );

  const togglePause = () =>
    schedule &&
    update.mutate(
      { is_active: !schedule.is_active },
      {
        onSuccess: () =>
          toast.success(schedule.is_active ? "Billing paused" : "Billing resumed"),
        onError: (e) => toast.error(e.message),
      },
    );

  const lines = schedule ? schedule.lines.map((l) => ({ description: l.description, amount: l.amount, vat_rate: l.vat_rate })) : suggested;
  const monthly = lines.reduce((s, l) => s + l.amount + l.amount * l.vat_rate, 0);

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Repeat className="size-4 text-accent" />
            Recurring billing
          </div>
          {schedule && (
            <span
              className={`text-xs ${schedule.is_active ? "text-emerald-600" : "text-muted-foreground"}`}
            >
              {schedule.is_active ? "Active" : "Paused"}
            </span>
          )}
        </div>

        {lines.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Set rent and service costs on this contract to enable recurring billing.
          </p>
        ) : (
          <>
            <table className="w-full text-sm">
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-1.5">{l.description}</td>
                    <td className="py-1.5 text-right text-xs text-muted-foreground">
                      {vatPct(l.vat_rate)} VAT
                    </td>
                    <td className="py-1.5 text-right tabular-nums">{money(l.amount)}</td>
                  </tr>
                ))}
                <tr className="font-medium">
                  <td className="py-2">Per month (incl. VAT)</td>
                  <td />
                  <td className="py-2 text-right tabular-nums">{money(monthly)}</td>
                </tr>
              </tbody>
            </table>

            {!schedule ? (
              <div className="mt-4 space-y-3 border-t pt-4">
                <p className="text-sm text-muted-foreground">
                  Bills monthly on day {suggestedDay}.
                </p>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={autoIssue}
                      onChange={(e) => setAutoIssue(e.target.checked)}
                    />
                    Auto-issue each month (post automatically)
                  </label>
                  <Button onClick={onStart} disabled={start.isPending}>
                    {start.isPending ? "Starting…" : "Start billing"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-4 space-y-2 border-t pt-4 text-sm">
                <p className="text-muted-foreground">
                  Bills monthly on day {schedule.day_of_month}
                  {schedule.auto_issue ? ", auto-issued" : ", as drafts for review"}.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={toggleAutoIssue}>
                    {schedule.auto_issue ? "Switch to draft-for-review" : "Switch to auto-issue"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={togglePause}>
                    {schedule.is_active ? "Pause billing" : "Resume billing"}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
